"use client";

import { FormEvent, useState } from "react";
import { signIn } from "next-auth/react";
import { useLocale, useTranslations } from "next-intl";
import { useRouter, useSearchParams } from "next/navigation";

export function LoginForm() {
  const t = useTranslations("auth");
  const locale = useLocale();
  const router = useRouter();
  const searchParams = useSearchParams();
  const [email, setEmail] = useState("admin@demo.local");
  const [password, setPassword] = useState("Admin123!");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    if (!email || !password) {
      setError(t("required"));
      return;
    }

    setLoading(true);
    const callbackUrl = searchParams.get("callbackUrl") || `/${locale}/dashboard`;
    const res = await signIn("credentials", {
      email,
      password,
      redirect: false,
    });
    setLoading(false);

    if (res?.error) {
      setError(res.error === "DISABLED" ? t("disabled") : t("invalid"));
      return;
    }

    router.push(callbackUrl);
    router.refresh();
  }

  return (
    <div className="login-shell min-h-screen lg:grid lg:grid-cols-[minmax(0,1.45fr)_minmax(22rem,0.9fr)]">
      <section className="login-brand relative hidden min-h-screen overflow-hidden lg:flex lg:items-center">
        <div className="login-brand-mesh" aria-hidden />
        <div className="login-brand-grid" aria-hidden />
        <div className="login-brand-orb login-brand-orb-a" aria-hidden />
        <div className="login-brand-orb login-brand-orb-b" aria-hidden />

        <div className="relative z-10 mx-auto grid w-full max-w-[58rem] gap-12 px-12 py-16 xl:grid-cols-[1.05fr_0.95fr] xl:items-center xl:gap-14 xl:px-16">
          <div className="fade-up">
            <p className="text-[0.78rem] font-bold uppercase tracking-[0.28em] text-white/70">
              DEPEI GEO
            </p>
            <h1 className="page-title mt-5 text-[clamp(3.2rem,5.6vw,4.8rem)] leading-[0.98] tracking-[-0.04em] text-white">
              {t("heroBrand")}
            </h1>
            <p className="mt-5 max-w-md text-[1.15rem] font-medium leading-snug text-white/92">
              {t("heroTitle")}
            </p>
            <p className="mt-4 max-w-md text-[0.98rem] leading-relaxed text-white/72">
              {t("heroDesc")}
            </p>

            <div className="login-flow mt-10">
              <span className="login-step-dot" />
              {t("heroPoint1")}
              <span className="login-step-line" />
              <span className="login-step-dot" />
              {t("heroPoint2")}
              <span className="login-step-line" />
              <span className="login-step-dot" />
              {t("heroPoint3")}
            </div>
          </div>

          <div className="fade-up" style={{ animationDelay: "120ms" }} aria-hidden>
            <div className="login-visual p-5">
              <div className="mb-4 flex items-center justify-between gap-3">
                <span className="login-visual-chip">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#9ff0e2]" />
                  AI Search
                </span>
                <span className="text-[0.7rem] tracking-wide text-white/55">GEO Signal</span>
              </div>
              <div className="rounded-2xl bg-gradient-to-br from-white/18 via-white/8 to-transparent p-4">
                <div className="login-visual-bar w-[72%]" />
                <div className="mt-3 login-visual-bar w-[48%] opacity-70" />
                <div className="mt-6 grid grid-cols-3 gap-3">
                  <div className="rounded-xl bg-white/12 p-3">
                    <p className="text-2xl font-semibold tracking-tight text-white">{t("proof1Value")}</p>
                    <p className="mt-1 text-[0.68rem] leading-snug text-white/65">{t("proof1Label")}</p>
                  </div>
                  <div className="rounded-xl bg-white/12 p-3">
                    <p className="text-2xl font-semibold tracking-tight text-white">{t("proof2Value")}</p>
                    <p className="mt-1 text-[0.68rem] leading-snug text-white/65">{t("proof2Label")}</p>
                  </div>
                  <div className="rounded-xl bg-white/12 p-3">
                    <p className="text-2xl font-semibold tracking-tight text-white">{t("proof3Value")}</p>
                    <p className="mt-1 text-[0.68rem] leading-snug text-white/65">{t("proof3Label")}</p>
                  </div>
                </div>
                <div className="mt-4 space-y-2">
                  <div className="h-2 rounded-full bg-white/10">
                    <div className="h-2 w-[78%] rounded-full bg-gradient-to-r from-[#19d3b8] to-[#38bdf8]" />
                  </div>
                  <div className="h-2 rounded-full bg-white/10">
                    <div className="h-2 w-[54%] rounded-full bg-gradient-to-r from-[#38bdf8] to-[#ff7a45]/80" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="relative flex min-h-screen items-center justify-center px-6 py-12">
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse at 80% 8%, rgba(0,168,148,0.1), transparent 42%), radial-gradient(ellipse at 10% 92%, rgba(0,144,212,0.08), transparent 40%)",
          }}
        />

        <div
          className="login-card fade-up relative z-10 w-full max-w-[420px]"
          style={{ animationDelay: "60ms" }}
        >
          <div className="mb-8 flex items-start justify-between gap-4 lg:hidden">
            <div>
              <p className="page-title text-2xl">{t("heroBrand")}</p>
              <p className="mt-1 text-sm text-[var(--fg-muted)]">{t("heroTag")}</p>
            </div>
          </div>

          <div className="mb-8">
            <p className="page-kicker mb-3">{t("loginKicker")}</p>
            <h2 className="page-title text-[1.85rem] tracking-tight">{t("loginTitle")}</h2>
            <p className="mt-2 max-w-[18rem] text-sm leading-relaxed text-[var(--fg-muted)]">
              {t("loginSubtitle")}
            </p>
          </div>

          <form onSubmit={onSubmit} className="space-y-5">
            <div>
              <label className="label" htmlFor="email">
                {t("email")}
              </label>
              <input
                id="email"
                className="input !py-3"
                type="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div>
              <label className="label" htmlFor="password">
                {t("password")}
              </label>
              <input
                id="password"
                className="input !py-3"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>

            {error ? (
              <p className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                {error}
              </p>
            ) : null}

            <button
              className="btn btn-primary btn-shine w-full !py-3.5 !text-[0.98rem]"
              type="submit"
              disabled={loading}
            >
              {loading ? "..." : t("submit")}
            </button>
          </form>

          <p className="mt-7 text-center text-xs leading-relaxed text-[var(--fg-muted)]">
            {t("loginFoot")}
          </p>
        </div>
      </section>
    </div>
  );
}
